import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  useViewport,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Server,
  Database,
  Zap,
  Cloud,
  Activity,
  DollarSign,
  Clock,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  X,
  Link,
  Wifi,
  MessageSquare,
  ArrowRightLeft,
  Search,
  ChevronUp,
  ChevronDown,
  Package,
  Layers,
  XCircle,
  Award,
  Target,
  Shield,
  Wrench,
  Eye,
  FlaskConical,
  Flame,
  Users,
  Smartphone,
  Monitor,
  Globe,
  Trash2,
  Save,
  Loader2,
  Maximize2,
  Minimize2,
  Sparkles,
  Brain,
  Copy,
  Lock,
  Crown,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useUserStore from '../store/userStore';
import useSimulationStore from '../store/simulationStore';
import { SimulationPanel, ChaosPanel, SimulationOverlay } from './simulation';
import { validateDesign, convertCanvasToDesignGraph } from '../validation';
import { componentsAPI, userAPI, economyAPI, challengesAPI } from '../services/api';
import { toast } from 'react-toastify';
import TutorialOverlay from './TutorialOverlay';
import {
  COMPONENT_ICONS,
  CATEGORY_COLORS,
  ComponentNode as SharedComponentNode,
  UsersNode as SharedUsersNode,
  NodeAnimationStyles,
} from './canvas/NodeComponents';

const PROTOCOLS = [
  { id: 'http', name: 'HTTP/REST', description: 'Standard HTTP API calls', color: '#3b82f6', icon: Link },
  { id: 'https', name: 'HTTPS', description: 'Secure HTTP with TLS', color: '#10b981', icon: Link },
  { id: 'grpc', name: 'gRPC', description: 'High-performance RPC', color: '#8b5cf6', icon: ArrowRightLeft },
  { id: 'websocket', name: 'WebSocket', description: 'Bidirectional real-time', color: '#f59e0b', icon: Wifi },
  { id: 'tcp', name: 'TCP', description: 'Raw TCP connection', color: '#6b7280', icon: Link },
  { id: 'amqp', name: 'AMQP', description: 'Message queue protocol', color: '#ec4899', icon: MessageSquare },
  { id: 'kafka', name: 'Kafka Protocol', description: 'Event streaming', color: '#000000', icon: MessageSquare },
  { id: 'redis', name: 'Redis Protocol', description: 'Cache/pub-sub', color: '#dc2626', icon: Zap },
  { id: 'sql', name: 'SQL/Database', description: 'Database queries', color: '#0ea5e9', icon: Database },
];

// Provider config for component details
const PROVIDER_CONFIG = {
  aws: { name: 'AWS', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-100' },
  azure: { name: 'Azure', color: 'bg-blue-600', textColor: 'text-blue-700', bgLight: 'bg-blue-100' },
  gcp: { name: 'GCP', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-100' },
  open_source: { name: 'Open Source', color: 'bg-green-600', textColor: 'text-green-700', bgLight: 'bg-green-100' },
  generic: { name: 'Generic', color: 'bg-gray-500', textColor: 'text-gray-700', bgLight: 'bg-gray-100' },
};

const RARITY_CONFIG = {
  common: { name: 'Common', color: 'bg-gray-100 text-gray-700 border-gray-300', glow: '' },
  uncommon: { name: 'Uncommon', color: 'bg-green-100 text-green-700 border-green-300', glow: '' },
  rare: { name: 'Rare', color: 'bg-blue-100 text-blue-700 border-blue-400', glow: 'shadow-blue-200' },
  epic: { name: 'Epic', color: 'bg-purple-100 text-purple-700 border-purple-400', glow: 'shadow-purple-300 shadow-lg' },
  legendary: { name: 'Legendary', color: 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 border-amber-400', glow: 'shadow-amber-300 shadow-xl' },
};

const TIER_CONFIG = {
  basic: { name: 'Basic', icon: '1' },
  standard: { name: 'Standard', icon: '2' },
  pro: { name: 'Pro', icon: '3' },
  enterprise: { name: 'Enterprise', icon: '4' },
};

// Full Component Detail Modal (same as inventory page)
const CanvasComponentDetailModal = ({ component, onClose, loading }) => {
  if (!component && !loading) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-gray-600">Loading component details...</span>
          </div>
        </div>
      </div>
    );
  }

  const Icon = COMPONENT_ICONS[component.category] || Server;
  const categoryColor = CATEGORY_COLORS[component.category] || '#6b7280';
  const provider = component.cost_profile?.provider || component.provider || 'generic';
  const providerConfig = PROVIDER_CONFIG[provider] || PROVIDER_CONFIG.generic;
  const costProfile = component.cost_profile || {};
  const rarity = component.rarity || 'common';
  const tier = component.tier || 'basic';
  const rarityConfig = RARITY_CONFIG[rarity] || RARITY_CONFIG.common;
  const tierConfig = TIER_CONFIG[tier] || TIER_CONFIG.basic;
  const stats = component.stats || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className={`bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl ${rarityConfig.glow}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 text-white relative" style={{ backgroundColor: categoryColor }}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
              <Icon className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-1">{component.name}</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${providerConfig.bgLight} ${providerConfig.textColor}`}>
                  {providerConfig.name}
                </span>
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-white/20 capitalize">
                  {component.category}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${rarityConfig.color}`}>
                  {rarityConfig.name}
                </span>
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-white/20">
                  T{tierConfig.icon}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Description</h3>
            <p className="text-gray-700 leading-relaxed">{component.description}</p>
          </div>

          {/* Cost Profile */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Performance & Cost</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {costProfile.monthly_cost !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <DollarSign className="w-3 h-3" />Monthly Cost
                  </div>
                  <div className="text-lg font-bold text-gray-900">${costProfile.monthly_cost}</div>
                </div>
              )}
              {costProfile.qps_capacity !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <Activity className="w-3 h-3" />QPS Capacity
                  </div>
                  <div className="text-lg font-bold text-gray-900">{costProfile.qps_capacity.toLocaleString()}</div>
                </div>
              )}
              {costProfile.latency_ms !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <Clock className="w-3 h-3" />Latency
                  </div>
                  <div className="text-lg font-bold text-gray-900">{costProfile.latency_ms}ms</div>
                </div>
              )}
              {costProfile.storage_gb !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <Database className="w-3 h-3" />Storage
                  </div>
                  <div className="text-lg font-bold text-gray-900">{costProfile.storage_gb.toLocaleString()} GB</div>
                </div>
              )}
              {costProfile.memory_gb !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <Server className="w-3 h-3" />Memory
                  </div>
                  <div className="text-lg font-bold text-gray-900">{costProfile.memory_gb} GB</div>
                </div>
              )}
              {costProfile.throughput_msg_sec !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <Activity className="w-3 h-3" />Throughput
                  </div>
                  <div className="text-lg font-bold text-gray-900">{costProfile.throughput_msg_sec.toLocaleString()} msg/s</div>
                </div>
              )}
            </div>
          </div>

          {/* Detailed Stats */}
          {stats && Object.keys(stats).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Detailed Stats</h3>
              <div className="space-y-4">
                {/* Performance Stats */}
                {stats.performance && Object.keys(stats.performance).length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-700 mb-2">Performance</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      {stats.performance.qps_read && (
                        <div><span className="text-gray-500">Read QPS:</span> <span className="font-medium">{stats.performance.qps_read.toLocaleString()}</span></div>
                      )}
                      {stats.performance.qps_write && (
                        <div><span className="text-gray-500">Write QPS:</span> <span className="font-medium">{stats.performance.qps_write.toLocaleString()}</span></div>
                      )}
                      {stats.performance.latency_p50_ms && (
                        <div><span className="text-gray-500">P50 Latency:</span> <span className="font-medium">{stats.performance.latency_p50_ms}ms</span></div>
                      )}
                      {stats.performance.latency_p99_ms && (
                        <div><span className="text-gray-500">P99 Latency:</span> <span className="font-medium">{stats.performance.latency_p99_ms}ms</span></div>
                      )}
                      {stats.performance.throughput_mbps && (
                        <div><span className="text-gray-500">Throughput:</span> <span className="font-medium">{stats.performance.throughput_mbps.toLocaleString()} MB/s</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Reliability Stats */}
                {stats.reliability && Object.keys(stats.reliability).length > 0 && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-green-700 mb-2">Reliability</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      {stats.reliability.sla_percentage && (
                        <div><span className="text-gray-500">SLA:</span> <span className="font-medium">{stats.reliability.sla_percentage}%</span></div>
                      )}
                      {stats.reliability.availability_zones && (
                        <div><span className="text-gray-500">AZs:</span> <span className="font-medium">{stats.reliability.availability_zones}</span></div>
                      )}
                      {stats.reliability.durability_nines && (
                        <div><span className="text-gray-500">Durability:</span> <span className="font-medium">{stats.reliability.durability_nines} 9s</span></div>
                      )}
                      {stats.reliability.auto_failover && (
                        <div><span className="text-gray-500">Failover:</span> <span className="font-medium text-green-600">Auto</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Scalability Stats */}
                {stats.scalability && Object.keys(stats.scalability).length > 0 && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-purple-700 mb-2">Scalability</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      {stats.scalability.horizontal_scaling && (
                        <div><span className="text-gray-500">Horizontal:</span> <span className="font-medium text-green-600">Yes</span></div>
                      )}
                      {stats.scalability.auto_scaling && (
                        <div><span className="text-gray-500">Auto-scale:</span> <span className="font-medium text-green-600">Yes</span></div>
                      )}
                      {stats.scalability.max_nodes && (
                        <div><span className="text-gray-500">Max Nodes:</span> <span className="font-medium">{stats.scalability.max_nodes.toLocaleString()}</span></div>
                      )}
                      {stats.scalability.max_instances && (
                        <div><span className="text-gray-500">Max Instances:</span> <span className="font-medium">{stats.scalability.max_instances.toLocaleString()}</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Security Stats */}
                {stats.security && Object.keys(stats.security).length > 0 && (
                  <div className="bg-red-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-red-700 mb-2">Security</h4>
                    <div className="flex flex-wrap gap-2">
                      {stats.security.encryption_at_rest && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Encryption at Rest</span>
                      )}
                      {stats.security.encryption_in_transit && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Encryption in Transit</span>
                      )}
                      {stats.security.compliance && stats.security.compliance.map(c => (
                        <span key={c} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">{c}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Features */}
          {Object.keys(costProfile).filter(k =>
            !['monthly_cost', 'qps_capacity', 'latency_ms', 'storage_gb', 'memory_gb', 'throughput_msg_sec', 'throughput_mb_sec', 'provider', 'cpu_cores', 'iops'].includes(k) &&
            typeof costProfile[k] !== 'number'
          ).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Features</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(costProfile).map(([key, value]) => {
                  if (['monthly_cost', 'qps_capacity', 'latency_ms', 'storage_gb', 'memory_gb', 'throughput_msg_sec', 'throughput_mb_sec', 'provider', 'cpu_cores', 'iops'].includes(key)) return null;
                  if (typeof value === 'boolean' && value) {
                    return (
                      <span key={key} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        {key.replace(/_/g, ' ')}
                      </span>
                    );
                  }
                  if (Array.isArray(value)) {
                    return (
                      <span key={key} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                        {key.replace(/_/g, ' ')}: {value.join(', ')}
                      </span>
                    );
                  }
                  if (typeof value === 'string' && value !== 'generic') {
                    return (
                      <span key={key} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                        {key.replace(/_/g, ' ')}: {value}
                      </span>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Users Node Configuration Modal
const UsersNodeConfigModal = ({ config, onSave, onClose }) => {
  const [label, setLabel] = useState(config.label || 'Users & Clients');
  const [users, setUsers] = useState(config.users || config.userCount || '50M');
  const [concurrent, setConcurrent] = useState(config.concurrent || config.concurrentSessions || '5M');

  const handleSave = () => {
    onSave({ label, users, concurrent });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-md w-full shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 text-white relative bg-gradient-to-r from-pink-500 to-purple-600 rounded-t-2xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Configure Users Node</h2>
              <p className="text-pink-100 text-sm">Set the traffic source configuration</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              placeholder="e.g., Users & Clients"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Users</label>
              <input
                type="text"
                value={users}
                onChange={(e) => setUsers(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                placeholder="e.g., 50M, 100K"
              />
              <p className="text-xs text-gray-500 mt-1">Use K for thousands, M for millions</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Concurrent Users</label>
              <input
                type="text"
                value={concurrent}
                onChange={(e) => setConcurrent(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                placeholder="e.g., 5M, 10K"
              />
              <p className="text-xs text-gray-500 mt-1">Peak concurrent sessions</p>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-pink-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-pink-800 mb-2">Preview</h4>
            <div className="flex items-center gap-4 text-sm">
              <div className="text-center">
                <div className="text-pink-600 font-bold text-lg">{users}</div>
                <div className="text-gray-600 text-xs">Total Users</div>
              </div>
              <div className="text-center">
                <div className="text-purple-600 font-bold text-lg">{concurrent}</div>
                <div className="text-gray-600 text-xs">Concurrent</div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-2 bg-pink-600 text-white rounded-lg font-medium hover:bg-pink-700 transition-colors"
            >
              Save Changes
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProtocolModal = ({ isOpen, onClose, onSelect, sourceNode, targetNode }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 text-white">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold">Select Protocol</h3>
            <button onClick={onClose} className="p-1 rounded hover:bg-white/20"><X className="w-5 h-5" /></button>
          </div>
          <p className="text-sm text-blue-100 mt-1">{sourceNode?.data?.label} → {targetNode?.data?.label}</p>
        </div>
        <div className="p-3 grid grid-cols-3 gap-2 max-h-80 overflow-y-auto">
          {PROTOCOLS.map(protocol => {
            const Icon = protocol.icon;
            return (
              <button
                key={protocol.id}
                onClick={() => onSelect(protocol)}
                className="flex flex-col items-center gap-1 p-3 rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: protocol.color + '20' }}>
                  <Icon className="w-4 h-4" style={{ color: protocol.color }} />
                </div>
                <div className="text-xs font-medium text-gray-900 text-center">{protocol.name}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Validation Results Modal - Shows after clicking Submit Design
const ValidationResultsModal = ({ isOpen, onClose, result, onSubmit, isSubmitting = false, isPublicPreview = false }) => {
  const [useAI, setUseAI] = useState(true); // Default to AI evaluation
  const [aiRateLimit, setAiRateLimit] = useState(null);
  const [loadingRateLimit, setLoadingRateLimit] = useState(false);

  // Fetch AI rate limit stats when modal opens (skip for anonymous users)
  useEffect(() => {
    if (isOpen && !isPublicPreview) {
      setLoadingRateLimit(true);
      userAPI.getAIRateLimit()
        .then(response => {
          setAiRateLimit(response.data);
          // Auto-disable AI if user can't use it
          if (!response.data.can_use_ai) {
            setUseAI(false);
          }
        })
        .catch(err => {
          console.error('Failed to fetch AI rate limit:', err);
          // Default to allowing AI on error
          setAiRateLimit(null);
        })
        .finally(() => setLoadingRateLimit(false));
    }
  }, [isOpen, isPublicPreview]);

  if (!isOpen || !result) return null;

  const { errors, warnings, isValid } = result;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Simple Header */}
        <div className={`bg-gradient-to-r ${isValid ? 'from-blue-600 to-purple-600' : 'from-red-600 to-orange-600'} p-4 text-white`}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold flex items-center gap-2">
              {isValid ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
              {isValid ? 'Submit Your Design' : `Fix ${errors.length} Error(s) to Submit`}
            </h3>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Errors Section */}
          {errors.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-red-700 mb-3 flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Errors ({errors.length}) - Must fix before submitting
              </h4>
              <div className="space-y-2">
                {errors.map((violation, idx) => (
                  <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-900">{violation.message}</p>
                        {violation.fixes && violation.fixes.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {violation.fixes.map((fix, fIdx) => (
                              <div key={fIdx} className="flex items-center gap-2 text-xs text-green-700">
                                <Wrench className="w-3 h-3" />
                                {fix.description}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warnings Section */}
          {warnings.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-orange-700 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Warnings ({warnings.length}) - Recommended improvements
              </h4>
              <div className="space-y-2">
                {warnings.map((violation, idx) => (
                  <div key={idx} className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-900">{violation.message}</p>
                        {violation.fixes && violation.fixes.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {violation.fixes.map((fix, fIdx) => (
                              <div key={fIdx} className="flex items-center gap-2 text-xs text-green-700">
                                <Wrench className="w-3 h-3" />
                                {fix.description}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Evaluation Toggle */}
          <div className={`mb-6 p-4 rounded-xl border ${
            aiRateLimit && !aiRateLimit.can_use_ai
              ? 'bg-gray-50 border-gray-200'
              : 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  aiRateLimit && !aiRateLimit.can_use_ai ? 'bg-gray-200' : 'bg-purple-100'
                }`}>
                  <Sparkles className={`w-5 h-5 ${
                    aiRateLimit && !aiRateLimit.can_use_ai ? 'text-gray-400' : 'text-purple-600'
                  }`} />
                </div>
                <div>
                  <h4 className={`font-semibold ${
                    aiRateLimit && !aiRateLimit.can_use_ai ? 'text-gray-400' : 'text-gray-900'
                  }`}>AI-Powered Evaluation</h4>
                  <p className={`text-xs ${
                    aiRateLimit && !aiRateLimit.can_use_ai ? 'text-gray-400' : 'text-gray-600'
                  }`}>Get detailed feedback from Claude AI on your design</p>
                </div>
              </div>
              <button
                onClick={() => {
                  if (aiRateLimit?.can_use_ai || !aiRateLimit) {
                    setUseAI(!useAI);
                  }
                }}
                disabled={aiRateLimit && !aiRateLimit.can_use_ai}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  aiRateLimit && !aiRateLimit.can_use_ai
                    ? 'bg-gray-200 cursor-not-allowed'
                    : useAI ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                    useAI && !(aiRateLimit && !aiRateLimit.can_use_ai) ? 'translate-x-8' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* AI Rate Limit Info */}
            {!loadingRateLimit && aiRateLimit && (
              <div className="mt-3 flex items-center justify-between text-xs">
                <div className="flex items-center gap-4">
                  <span className={`px-2 py-1 rounded-full font-medium ${
                    aiRateLimit.remaining_daily > 0
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {aiRateLimit.remaining_daily}/{aiRateLimit.daily_limit} daily
                  </span>
                  {aiRateLimit.is_premium && (
                    <span className={`px-2 py-1 rounded-full font-medium ${
                      aiRateLimit.remaining_hourly > 0
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {aiRateLimit.remaining_hourly}/{aiRateLimit.hourly_limit} hourly
                    </span>
                  )}
                  {aiRateLimit.cooldown_seconds > 0 && (
                    <span className="px-2 py-1 rounded-full font-medium bg-yellow-100 text-yellow-700">
                      Wait {aiRateLimit.cooldown_seconds}s
                    </span>
                  )}
                </div>
                {aiRateLimit.is_premium && (
                  <span className="px-2 py-0.5 rounded bg-gradient-to-r from-amber-400 to-yellow-500 text-white font-medium text-[10px]">
                    PREMIUM
                  </span>
                )}
              </div>
            )}
            {loadingRateLimit && (
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Checking AI availability...</span>
              </div>
            )}

            {/* Status Messages */}
            {useAI && aiRateLimit?.can_use_ai && (
              <div className="mt-3 flex items-center gap-2 text-xs text-purple-700 bg-purple-100 rounded-lg px-3 py-2">
                <Brain className="w-4 h-4" />
                <span>Your design will be evaluated based on real-world viability, trade-offs, and best practices</span>
              </div>
            )}
            {!useAI && (
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-600 bg-gray-100 rounded-lg px-3 py-2">
                <Zap className="w-4 h-4" />
                <span>Quick rule-based scoring (faster, but less detailed feedback)</span>
              </div>
            )}
            {aiRateLimit && !aiRateLimit.can_use_ai && (
              <div className="mt-3 flex items-center gap-2 text-xs text-orange-700 bg-orange-100 rounded-lg px-3 py-2">
                <AlertTriangle className="w-4 h-4" />
                <span>
                  {aiRateLimit.cooldown_seconds > 0
                    ? `Please wait ${aiRateLimit.cooldown_seconds} seconds before next AI evaluation`
                    : aiRateLimit.remaining_daily === 0
                      ? 'Daily AI evaluation limit reached. Using rule-based scoring.'
                      : 'Hourly AI evaluation limit reached. Try again later or use rule-based scoring.'}
                  {!aiRateLimit.is_premium && aiRateLimit.remaining_daily === 0 && (
                    <span className="ml-1 font-medium">Upgrade to Premium for more!</span>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* No issues message */}
          {errors.length === 0 && warnings.length === 0 && (
            <div className="text-center py-8">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-900">Excellent design!</p>
              <p className="text-gray-500">No issues found. Ready to submit.</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t p-4 flex items-center justify-between bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors"
          >
            Go Back & Edit
          </button>
          <button
            onClick={() => onSubmit(useAI)}
            disabled={!isValid || isSubmitting}
            className={`px-6 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
              isSubmitting
                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white opacity-80'
                : isValid
                  ? useAI
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:shadow-lg'
                    : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:shadow-lg'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Evaluating...
              </>
            ) : (
              <>
                {useAI ? <Sparkles className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
                {isValid ? (useAI ? 'Submit with AI Evaluation' : 'Submit') : 'Fix Errors First'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Node types using shared components from canvas/NodeComponents.jsx
// These components are shared with PublicChallengePage for consistent styling
const nodeTypes = {
  component: SharedComponentNode,
  users: SharedUsersNode,
  client: SharedUsersNode,
};

// Upgrade Prompt Modal for Premium Components
const UpgradePromptModal = ({ onClose }) => {
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-slate-800 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-700"
        onClick={e => e.stopPropagation()}
      >
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Premium Component</h3>
          <p className="text-slate-400 mb-6">
            This component requires a Premium subscription. Upgrade to access all cloud provider components and advanced features.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors"
            >
              Maybe Later
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-colors"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Check if component is premium locked for user
const isComponentLocked = (component, user) => {
  // If user is premium, nothing is locked
  if (user?.is_premium) return false;
  // Use backend flags if available
  if (component.can_access !== undefined) return !component.can_access;
  if (component.is_premium_required !== undefined) return component.is_premium_required;
  // Fallback: non-starter components are premium
  return !component.is_starter;
};

// Bottom Dock Component Browser
const ComponentDock = ({ inventory, onAddComponent, isExpanded, onToggle, user, onShowUpgradeModal }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const groupedComponents = useMemo(() => {
    const groups = {};
    // Ensure inventory is an array before iterating
    const items = Array.isArray(inventory) ? inventory : [];
    items.forEach(component => {
      const cat = component.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(component);
    });
    return groups;
  }, [inventory]);

  const categories = Object.keys(groupedComponents).sort();

  const filteredComponents = useMemo(() => {
    const items = Array.isArray(inventory) ? inventory : [];
    let components = selectedCategory === 'all' ? items : (groupedComponents[selectedCategory] || []);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      components = components.filter(c =>
        c.name.toLowerCase().includes(term) ||
        (c.cost_profile?.provider || '').toLowerCase().includes(term)
      );
    }
    return components;
  }, [inventory, selectedCategory, searchTerm, groupedComponents]);

  const handleComponentClick = (component) => {
    const isLocked = isComponentLocked(component, user);
    if (isLocked) {
      onShowUpgradeModal();
    } else {
      onAddComponent(component);
    }
  };

  // Count locked/unlocked for display
  const items = Array.isArray(inventory) ? inventory : [];
  const unlockedCount = items.filter(c => !isComponentLocked(c, user)).length;
  const lockedCount = items.filter(c => isComponentLocked(c, user)).length;

  return (
    <div className={`bg-white border-t shadow-lg transition-all duration-300 ${isExpanded ? 'h-48' : 'h-12'}`}>
      {/* Dock Header */}
      <div className="h-12 px-4 flex items-center gap-4 border-b bg-gray-50">
        <button onClick={onToggle} className="flex items-center gap-2 text-gray-700 hover:text-blue-600 font-medium">
          <Layers className="w-4 h-4" />
          <span className="text-sm">
            Components ({unlockedCount})
            {lockedCount > 0 && !user?.is_premium && (
              <span className="ml-1 text-amber-600">+{lockedCount} <Lock className="w-3 h-3 inline" /></span>
            )}
          </span>
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>

        {isExpanded && (
          <>
            {/* Search */}
            <div className="relative flex-shrink-0 w-48">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border rounded-lg"
              />
            </div>

            {/* Category Pills */}
            <div className="flex gap-1 overflow-x-auto flex-1">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                  selectedCategory === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap capitalize transition-colors`}
                  style={{
                    backgroundColor: selectedCategory === cat ? CATEGORY_COLORS[cat] : CATEGORY_COLORS[cat] + '30',
                    color: selectedCategory === cat ? 'white' : CATEGORY_COLORS[cat]
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Component Grid */}
      {isExpanded && (
        <div className="h-[calc(100%-48px)] overflow-x-auto overflow-y-hidden p-3">
          <div className="flex gap-2 h-full">
            {filteredComponents.map((component) => {
              const Icon = COMPONENT_ICONS[component.category] || Server;
              const color = CATEGORY_COLORS[component.category] || '#6b7280';
              const isLocked = isComponentLocked(component, user);
              return (
                <button
                  key={component.id}
                  onClick={() => handleComponentClick(component)}
                  className={`relative flex-shrink-0 w-32 h-full p-2 rounded-lg border-2 transition-all flex flex-col items-center justify-center gap-1 group ${
                    isLocked
                      ? 'border-amber-300 bg-amber-50/50 hover:border-amber-400'
                      : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                  }`}
                >
                  {/* Lock Badge */}
                  {isLocked && (
                    <div className="absolute -top-1.5 -right-1.5 z-10">
                      <div className="w-5 h-5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center shadow">
                        <Lock className="w-2.5 h-2.5 text-white" />
                      </div>
                    </div>
                  )}
                  <div
                    className={`p-2 rounded-lg transition-colors ${isLocked ? 'grayscale opacity-60' : ''}`}
                    style={{ backgroundColor: color + '20' }}
                  >
                    <Icon className="w-5 h-5" style={{ color: isLocked ? '#9ca3af' : color }} />
                  </div>
                  <div className={`text-xs font-medium text-center truncate w-full ${isLocked ? 'text-gray-400' : 'text-gray-900'}`}>
                    {component.name}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    {isLocked ? (
                      <span className="text-amber-600 flex items-center gap-0.5">
                        <Crown className="w-2.5 h-2.5" /> Premium
                      </span>
                    ) : (
                      component.cost_profile?.qps_capacity
                        ? `${(component.cost_profile.qps_capacity/1000).toFixed(0)}k QPS`
                        : `$${component.cost_profile?.monthly_cost || 0}/mo`
                    )}
                  </div>
                </button>
              );
            })}
            {filteredComponents.length === 0 && (
              <div className="flex items-center justify-center w-full text-gray-500 text-sm">
                No components found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DesignCanvas = ({
  challenge,
  onSubmit,
  initialDesign,
  referenceSolution,
  isPublicPreview = false,
  onLoginRequired,
  isAdminMode = false,
  solutionLocked = false,
  solutionUnlockCost = 75,
  onSolutionUnlocked,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [dockExpanded, setDockExpanded] = useState(true);
  const [designRestored, setDesignRestored] = useState(false);

  const { inventory, user } = useUserStore();
  const [allComponents, setAllComponents] = useState([]);
  const [metrics, setMetrics] = useState({ totalQPS: 0, monthlyCost: 0, avgLatency: 0, componentCount: 0 });
  const [showHint, setShowHint] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [protocolModal, setProtocolModal] = useState({ isOpen: false, pendingConnection: null, sourceNode: null, targetNode: null });

  // Validation modal state - shown on submit click
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Simulation state
  const [showSimulation, setShowSimulation] = useState(false);
  const [simulationTab, setSimulationTab] = useState('traffic'); // 'traffic' | 'chaos'
  const { simulationState, clearResults } = useSimulationStore();
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  // Component detail modal state
  const [selectedNodeDetails, setSelectedNodeDetails] = useState(null);
  const [fullComponentDetails, setFullComponentDetails] = useState(null);
  const [componentDetailsLoading, setComponentDetailsLoading] = useState(false);

  // Users node config modal state
  const [usersNodeConfig, setUsersNodeConfig] = useState(null);

  // Save draft state
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // Toggle between user design and reference solution
  const [viewingMode, setViewingMode] = useState('user'); // 'user' | 'solution'
  const [savedUserDesign, setSavedUserDesign] = useState(null);
  const [autoToggledSolution, setAutoToggledSolution] = useState(false);

  // Premium upgrade modal
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Solution unlock state
  const [unlockingSolution, setUnlockingSolution] = useState(false);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const canvasContainerRef = useRef(null);

  // Tutorial state (for challenge 11)
  const [showTutorial, setShowTutorial] = useState(false);

  // Toggle fullscreen mode
  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      // Enter fullscreen
      if (canvasContainerRef.current?.requestFullscreen) {
        canvasContainerRef.current.requestFullscreen();
      } else if (canvasContainerRef.current?.webkitRequestFullscreen) {
        canvasContainerRef.current.webkitRequestFullscreen();
      } else if (canvasContainerRef.current?.msRequestFullscreen) {
        canvasContainerRef.current.msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  }, [isFullscreen]);

  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Trigger tutorial for challenge 11 on first visit
  useEffect(() => {
    if (challenge?.id === 11) {
      const tutorialCompleted = localStorage.getItem('tutorial-challenge-11-completed');
      if (!tutorialCompleted) {
        // Small delay to let the canvas render first
        const timer = setTimeout(() => setShowTutorial(true), 500);
        return () => clearTimeout(timer);
      }
    }
  }, [challenge?.id]);

  // Handle tutorial completion
  const handleTutorialComplete = useCallback(() => {
    localStorage.setItem('tutorial-challenge-11-completed', 'true');
    setShowTutorial(false);
  }, []);

  // Fetch all components for public preview or admin mode
  useEffect(() => {
    if (isPublicPreview || isAdminMode) {
      componentsAPI.getAll()
        .then(response => {
          // API returns { components: [...], by_category: {...}, total: N }
          setAllComponents(response.data?.components || []);
        })
        .catch(err => {
          console.error('Failed to fetch components:', err);
        });
    }
  }, [isPublicPreview, isAdminMode]);

  // Helper to add callbacks to nodes
  const addNodeCallbacks = useCallback((nodeList) => {
    return nodeList.map(node => {
      const isUsersNode = node.type === 'users' || node.data?.category === 'users' || node.data?.nodeType === 'users';
      return {
        ...node,
        data: {
          ...node.data,
          // Don't allow removal in public preview mode
          onRemove: isPublicPreview ? undefined : () => {
            setNodes((nds) => nds.filter((n) => n.id !== node.id));
            setEdges((eds) => eds.filter((e) => e.source !== node.id && e.target !== node.id));
          },
          // Users node is only configurable in admin mode
          onShowDetails: isUsersNode && !isAdminMode ? undefined : (details) => {
            if (details.nodeType === 'users') {
              setUsersNodeConfig(details);
            } else {
              setSelectedNodeDetails(details);
            }
          },
        },
      };
    });
  }, [setNodes, setEdges, isPublicPreview, isAdminMode]);

  // Auto-populate User component for challenge 11 if user has no design
  useEffect(() => {
    if (challenge?.id === 11 && referenceSolution && designRestored && !autoToggledSolution && nodes.length === 0) {
      // User has no design - add only the User component as a starting point
      const timer = setTimeout(() => {
        try {
          const solution = typeof referenceSolution === 'string'
            ? JSON.parse(referenceSolution)
            : referenceSolution;

          // Find the User/Client node from the reference solution
          const userNode = solution.nodes?.find(n => n.type === 'users' || n.data?.category === 'users');

          if (userNode) {
            // Add only the User component centered on canvas
            const starterNode = {
              ...userNode,
              position: { x: 100, y: 250 }
            };
            setNodes(addNodeCallbacks([starterNode]));
          }
          setAutoToggledSolution(true);
        } catch (err) {
          console.error('Failed to auto-load starter component:', err);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [challenge?.id, referenceSolution, designRestored, autoToggledSolution, nodes.length, addNodeCallbacks, setNodes]);

  // Toggle between user design and reference solution
  const handleToggleSolution = useCallback(() => {
    if (!referenceSolution) return;

    if (viewingMode === 'user') {
      // Save current user design before switching to solution
      const currentDesign = {
        nodes: nodes.map(n => ({
          id: n.id,
          type: n.type,
          position: n.position,
          data: {
            label: n.data.label,
            description: n.data.description,
            category: n.data.category,
            componentId: n.data.componentId,
            qps: n.data.qps,
            monthlyCost: n.data.monthlyCost,
            latency: n.data.latency,
            users: n.data.users,
            concurrent: n.data.concurrent,
          },
        })),
        edges: edges.map(e => ({
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle,
          targetHandle: e.targetHandle,
          animated: e.animated,
          style: e.style,
          markerEnd: e.markerEnd,
          label: e.label,
          labelStyle: e.labelStyle,
          labelBgStyle: e.labelBgStyle,
          labelBgPadding: e.labelBgPadding,
          labelBgBorderRadius: e.labelBgBorderRadius,
          data: e.data,
        })),
      };
      setSavedUserDesign(currentDesign);

      // Load reference solution
      try {
        const solution = typeof referenceSolution === 'string'
          ? JSON.parse(referenceSolution)
          : referenceSolution;

        if (solution.nodes && Array.isArray(solution.nodes)) {
          setNodes(addNodeCallbacks(solution.nodes));
        }
        if (solution.edges && Array.isArray(solution.edges)) {
          setEdges(solution.edges);
        }
        setViewingMode('solution');
      } catch (err) {
        console.error('Failed to load reference solution:', err);
      }
    } else {
      // Restore user design
      if (savedUserDesign) {
        if (savedUserDesign.nodes && Array.isArray(savedUserDesign.nodes)) {
          setNodes(addNodeCallbacks(savedUserDesign.nodes));
        }
        if (savedUserDesign.edges && Array.isArray(savedUserDesign.edges)) {
          setEdges(savedUserDesign.edges);
        }
      }
      setViewingMode('user');
    }
  }, [referenceSolution, viewingMode, nodes, edges, savedUserDesign, addNodeCallbacks, setNodes, setEdges]);

  // Handle unlocking a reference solution with DP
  const handleUnlockSolution = useCallback(async () => {
    if (!challenge?.id) return;

    setUnlockingSolution(true);
    try {
      // Call the economy API to unlock the solution
      const unlockResponse = await economyAPI.unlockSolution(challenge.id);

      // Update user's DP balance in the store
      if (unlockResponse.data.new_dp_balance !== undefined) {
        useUserStore.getState().updateDesignPoints(unlockResponse.data.new_dp_balance);
      }

      // Fetch the challenge again to get the solution
      const challengeResponse = await challengesAPI.getById(challenge.id);
      const solutionJson = challengeResponse.data.solution_json;

      if (solutionJson && onSolutionUnlocked) {
        onSolutionUnlocked(solutionJson);
        toast.success(`Solution unlocked! -${unlockResponse.data.dp_spent || solutionUnlockCost} DP`);
      } else {
        toast.success('Solution unlocked!');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to unlock solution';
      const details = error.response?.data?.details;
      if (details?.required && details?.current !== undefined) {
        toast.error(`${errorMsg}. Need ${details.required} DP, have ${details.current} DP`);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setUnlockingSolution(false);
    }
  }, [challenge?.id, solutionUnlockCost, onSolutionUnlocked]);

  // Copy reference solution to user's canvas (merge, not replace)
  const handleCopySolutionToCanvas = useCallback(() => {
    if (!referenceSolution) return;

    try {
      const solution = typeof referenceSolution === 'string'
        ? JSON.parse(referenceSolution)
        : referenceSolution;

      // Get user's current design (either from savedUserDesign or empty)
      const userNodes = savedUserDesign?.nodes || [];
      const userEdges = savedUserDesign?.edges || [];

      // Generate new IDs for solution nodes to avoid conflicts
      const timestamp = Date.now();
      const offsetX = 500; // Offset solution nodes to the right

      const idMap = {};
      const solutionNodes = (solution.nodes || []).map((n, idx) => {
        const newId = `copied-${n.id}-${timestamp}-${idx}`;
        idMap[n.id] = newId;
        return {
          ...n,
          id: newId,
          position: { x: (n.position?.x || 0) + offsetX, y: n.position?.y || 0 },
        };
      });

      // Update edge references to new node IDs
      const solutionEdges = (solution.edges || []).map((e, idx) => ({
        ...e,
        id: `copied-${e.id}-${timestamp}-${idx}`,
        source: idMap[e.source] || e.source,
        target: idMap[e.target] || e.target,
      }));

      // Merge both designs
      const mergedNodes = [...addNodeCallbacks(userNodes), ...addNodeCallbacks(solutionNodes)];
      const mergedEdges = [...userEdges, ...solutionEdges];

      setNodes(mergedNodes);
      setEdges(mergedEdges);
      setViewingMode('user');
      setSavedUserDesign(null);
    } catch (err) {
      console.error('Failed to copy solution to canvas:', err);
    }
  }, [referenceSolution, savedUserDesign, addNodeCallbacks, setNodes, setEdges]);

  // Keyboard shortcut for fullscreen (Escape to exit, F11 to toggle)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F11') {
        e.preventDefault();
        toggleFullscreen();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleFullscreen]);

  // Fetch full component details when node is clicked
  useEffect(() => {
    if (selectedNodeDetails?.componentId) {
      setComponentDetailsLoading(true);
      setFullComponentDetails(null);
      componentsAPI.getById(selectedNodeDetails.componentId)
        .then(response => {
          setFullComponentDetails(response.data);
          setComponentDetailsLoading(false);
        })
        .catch(err => {
          console.error('Failed to fetch component details:', err);
          // Fallback to the basic details we have from the node
          setFullComponentDetails({
            name: selectedNodeDetails.label,
            description: selectedNodeDetails.description || '',
            category: selectedNodeDetails.category,
            cost_profile: {
              qps_capacity: selectedNodeDetails.qps,
              monthly_cost: selectedNodeDetails.monthlyCost,
              latency_ms: selectedNodeDetails.latency,
            },
          });
          setComponentDetailsLoading(false);
        });
    } else if (selectedNodeDetails) {
      // If no componentId, just use basic details
      setFullComponentDetails({
        name: selectedNodeDetails.label,
        description: selectedNodeDetails.description || '',
        category: selectedNodeDetails.category,
        cost_profile: {
          qps_capacity: selectedNodeDetails.qps,
          monthly_cost: selectedNodeDetails.monthlyCost,
          latency_ms: selectedNodeDetails.latency,
        },
      });
      setComponentDetailsLoading(false);
    } else {
      setFullComponentDetails(null);
      setComponentDetailsLoading(false);
    }
  }, [selectedNodeDetails]);

  // Get design JSON for simulation
  const getDesignJson = useCallback(() => {
    return {
      nodes: nodes.map(n => ({
        id: n.id,
        type: n.type,
        position: n.position,
        data: n.data,
      })),
      edges: edges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        data: e.data,
      })),
    };
  }, [nodes, edges]);

  // Get component list for simulation
  const componentList = useMemo(() => {
    return [...new Set(nodes.map(n => n.data.componentId).filter(Boolean))];
  }, [nodes]);

  // Restore design from draft (priority) or saved submission
  useEffect(() => {
    if (designRestored || !challenge?.id) return;

    const restoreDesign = (design) => {
      if (design.nodes && Array.isArray(design.nodes)) {
        // Add onRemove and onShowDetails callbacks to each restored node
        const nodesWithCallbacks = design.nodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            onRemove: () => {
              setNodes((nds) => nds.filter((n) => n.id !== node.id));
              setEdges((eds) => eds.filter((e) => e.source !== node.id && e.target !== node.id));
            },
            onShowDetails: (details) => setSelectedNodeDetails(details),
          },
        }));
        setNodes(nodesWithCallbacks);
      }
      if (design.edges && Array.isArray(design.edges)) {
        setEdges(design.edges);
      }
    };

    // For public preview or admin mode, skip draft loading and just use initialDesign
    if (isPublicPreview || isAdminMode) {
      if (initialDesign) {
        try {
          const design = typeof initialDesign === 'string' ? JSON.parse(initialDesign) : initialDesign;
          restoreDesign(design);
        } catch (err) {
          console.error('Failed to restore design:', err);
        }
      }
      setDesignRestored(true);
      return;
    }

    // For regular users, try to load draft first (drafts have priority over submissions)
    console.log('Loading draft for challenge:', challenge.id);
    userAPI.getDraft(challenge.id)
      .then(response => {
        console.log('Draft API response:', response.data);
        if (response.data?.draft?.design_json) {
          // Draft found - use it
          const design = response.data.draft.design_json;
          console.log('Restoring draft with nodes:', design.nodes?.length, 'edges:', design.edges?.length);
          restoreDesign(design);
          setDesignRestored(true);
        } else if (initialDesign) {
          // No draft - fall back to initial design (previous submission)
          try {
            const design = typeof initialDesign === 'string' ? JSON.parse(initialDesign) : initialDesign;
            restoreDesign(design);
          } catch (err) {
            console.error('Failed to restore design:', err);
          }
          setDesignRestored(true);
        } else {
          // No draft and no initial design - start with default Users node
          const defaultUsersNode = {
            id: 'users-1',
            type: 'users',
            position: { x: 100, y: 300 },
            data: {
              label: 'Users',
              category: 'users',
              qps: 1000,
              latency: 0,
              monthlyCost: 0,
            },
          };
          setNodes([defaultUsersNode]);
          setDesignRestored(true);
        }
      })
      .catch(err => {
        console.error('Failed to load draft:', err);
        // Fall back to initial design if draft loading fails
        if (initialDesign) {
          try {
            const design = typeof initialDesign === 'string' ? JSON.parse(initialDesign) : initialDesign;
            restoreDesign(design);
          } catch (parseErr) {
            console.error('Failed to restore design:', parseErr);
          }
        } else {
          // No initial design - start with default Users node
          const defaultUsersNode = {
            id: 'users-1',
            type: 'users',
            position: { x: 100, y: 300 },
            data: {
              label: 'Users',
              category: 'users',
              qps: 1000,
              latency: 0,
              monthlyCost: 0,
            },
          };
          setNodes([defaultUsersNode]);
        }
        setDesignRestored(true);
      });
  }, [initialDesign, designRestored, challenge?.id, setNodes, setEdges, isPublicPreview, isAdminMode]);

  // In admin mode, update canvas when initialDesign changes (e.g., from JSON editor)
  const initialDesignKey = isAdminMode ? JSON.stringify(initialDesign) : null;
  useEffect(() => {
    if (!isAdminMode || !initialDesign) return;

    try {
      const design = typeof initialDesign === 'string' ? JSON.parse(initialDesign) : initialDesign;
      if (design.nodes && Array.isArray(design.nodes)) {
        setNodes(design.nodes);
      }
      if (design.edges && Array.isArray(design.edges)) {
        setEdges(design.edges);
      }
    } catch (err) {
      console.error('Failed to update design from JSON:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminMode, initialDesignKey, setNodes, setEdges]);

  useEffect(() => {
    const totalQPS = nodes.reduce((sum, n) => sum + (n.data.qps || 0), 0);
    const monthlyCost = nodes.reduce((sum, n) => sum + (n.data.monthlyCost || 0), 0);
    const avgLatency = nodes.length > 0 ? nodes.reduce((sum, n) => sum + (n.data.latency || 50), 0) / nodes.length : 0;
    setMetrics({ totalQPS, monthlyCost, avgLatency: Math.round(avgLatency), componentCount: nodes.length });
  }, [nodes]);

  const onConnect = useCallback((params) => {
    const sourceNode = nodes.find(n => n.id === params.source);
    const targetNode = nodes.find(n => n.id === params.target);
    setProtocolModal({ isOpen: true, pendingConnection: params, sourceNode, targetNode });
  }, [nodes]);

  const handleProtocolSelect = (protocol) => {
    const { pendingConnection } = protocolModal;
    if (!pendingConnection) return;
    setEdges((eds) => addEdge({
      ...pendingConnection,
      animated: true,
      style: { stroke: protocol.color, strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: protocol.color },
      label: protocol.name,
      labelStyle: { fill: protocol.color, fontWeight: 600, fontSize: 10 },
      labelBgStyle: { fill: 'white', fillOpacity: 0.9 },
      labelBgPadding: [3, 3],
      labelBgBorderRadius: 3,
      data: { protocol: protocol.id, protocolName: protocol.name },
    }, eds));
    setProtocolModal({ isOpen: false, pendingConnection: null, sourceNode: null, targetNode: null });
  };

  const removeNode = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
  }, [setNodes, setEdges]);

  const addComponentToCanvas = (component) => {
    const nodeId = `${component.id}-${Date.now()}`;
    setNodes((nds) => [...nds, {
      id: nodeId,
      type: 'component',
      position: { x: Math.random() * 600 + 100, y: Math.random() * 300 + 50 },
      data: {
        label: component.name,
        description: component.description,
        category: component.category,
        componentId: component.id,
        qps: component.cost_profile?.qps_capacity || 0,
        monthlyCost: component.cost_profile?.monthly_cost || 0,
        latency: component.cost_profile?.latency_ms || 50,
        onRemove: () => removeNode(nodeId),
        onShowDetails: (details) => setSelectedNodeDetails(details),
      },
    }]);
  };

  // Clear all nodes and edges from canvas
  const handleClearCanvas = useCallback(() => {
    setNodes([]);
    setEdges([]);
  }, [setNodes, setEdges]);

  // Update a users node's configuration
  const handleUpdateUsersNode = useCallback((nodeId, newData) => {
    setNodes((nds) => nds.map((node) => {
      if (node.id === nodeId) {
        return {
          ...node,
          data: {
            ...node.data,
            label: newData.label,
            users: newData.users,
            concurrent: newData.concurrent,
          },
        };
      }
      return node;
    }));
    setUsersNodeConfig(null);
  }, [setNodes]);

  // Build draft JSON from current state
  const buildDraftJson = useCallback(() => {
    return {
      nodes: nodes.map(n => ({
        id: n.id,
        type: n.type,
        position: n.position,
        data: {
          label: n.data.label,
          description: n.data.description,
          category: n.data.category,
          componentId: n.data.componentId,
          qps: n.data.qps,
          monthlyCost: n.data.monthlyCost,
          latency: n.data.latency,
          // Users node specific data
          users: n.data.users,
          concurrent: n.data.concurrent,
        },
      })),
      edges: edges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        animated: e.animated,
        style: e.style,
        markerEnd: e.markerEnd,
        label: e.label,
        labelStyle: e.labelStyle,
        labelBgStyle: e.labelBgStyle,
        labelBgPadding: e.labelBgPadding,
        labelBgBorderRadius: e.labelBgBorderRadius,
        data: e.data,
      })),
    };
  }, [nodes, edges]);

  // Save draft design
  const handleSaveDraft = useCallback(async () => {
    if (!challenge?.id || nodes.length === 0) return;

    // If public preview, prompt login instead
    if (isPublicPreview && onLoginRequired) {
      onLoginRequired();
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      const designJson = buildDraftJson();
      await userAPI.saveDraft(challenge.id, designJson);
      setSaveMessage({ type: 'success', text: 'Draft saved!' });
      setTimeout(() => setSaveMessage(null), 2000);
    } catch (error) {
      console.error('Failed to save draft:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save draft' });
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setIsSaving(false);
    }
  }, [challenge?.id, nodes.length, buildDraftJson, isPublicPreview, onLoginRequired]);

  // Run validation and show modal when clicking Submit Design
  const handleSubmitClick = useCallback(() => {
    if (nodes.length === 0) return;

    // Convert canvas to design graph format
    const designGraph = convertCanvasToDesignGraph(nodes, edges, challenge?.id || 'default');

    // Run validation
    const result = validateDesign(designGraph, challenge);
    setValidationResult(result);
    setShowValidationModal(true);
  }, [nodes, edges, challenge]);

  // Actually submit the design (called from modal when user confirms)
  const handleConfirmSubmit = useCallback(async (useAI = true) => {
    // Auto-save draft FIRST before anything else (skip for public preview - anonymous users)
    if (!isPublicPreview && challenge?.id && nodes.length > 0) {
      try {
        const draftJson = buildDraftJson();
        console.log('Saving draft on submit for challenge:', challenge.id, 'nodes:', nodes.length);
        const saveResponse = await userAPI.saveDraft(challenge.id, draftJson);
        console.log('Draft auto-saved on submit, response:', saveResponse.data);
      } catch (error) {
        console.error('Failed to auto-save draft on submit:', error);
        // Don't block submission if draft save fails
      }
    } else if (!isPublicPreview) {
      console.warn('Cannot save draft - challenge.id:', challenge?.id, 'nodes.length:', nodes.length);
    }

    const design = {
      nodes, edges,
      connections: edges.map(e => ({ source: e.source, target: e.target, protocol: e.data?.protocol || 'http' })),
      estimated_qps: metrics.totalQPS,
      estimated_latency_ms: metrics.avgLatency,
      single_points_of_failure: nodes.length < 2,
      uses_realtime_communication: edges.some(e => e.data?.protocol === 'websocket'),
      uses_grpc: edges.some(e => e.data?.protocol === 'grpc'),
      uses_message_queue: edges.some(e => ['amqp', 'kafka'].includes(e.data?.protocol)),
      uses_distributed_architecture: nodes.length > 3,
      validation_score: validationResult?.finalScore || 0,
      validation_grade: validationResult?.grade || 'F',
    };

    // Set submitting state to show loading spinner
    setIsSubmitting(true);

    try {
      // Call parent's onSubmit and wait for it to complete
      await onSubmit?.({ design_json: design, component_list: [...new Set(nodes.map(n => n.data.componentId))], use_ai: useAI });
    } finally {
      // Close modal and reset state after submission completes (success or error)
      setIsSubmitting(false);
      setShowValidationModal(false);
    }
  }, [challenge?.id, nodes, edges, metrics, validationResult, buildDraftJson, onSubmit, isPublicPreview]);

  const hasCache = nodes.some(n => n.data.category === 'cache');
  const hasDatabase = nodes.some(n => n.data.category === 'database');
  const hasLoadBalancer = nodes.some(n => n.data.category === 'loadbalancer');
  const meetsQPS = metrics.totalQPS >= (challenge?.requirements?.qps_read || 0);
  const withinBudget = metrics.monthlyCost <= (challenge?.score_criteria?.cost?.monthly_budget || 1000);

  return (
    <div
      ref={canvasContainerRef}
      className={`flex bg-gray-100 overflow-hidden transition-all duration-300 ${
        isFullscreen
          ? 'fixed inset-0 z-[9999] h-screen w-screen rounded-none'
          : 'h-[800px] rounded-xl'
      }`}
    >
      <NodeAnimationStyles />
      <ProtocolModal isOpen={protocolModal.isOpen}
        onClose={() => setProtocolModal({ isOpen: false, pendingConnection: null, sourceNode: null, targetNode: null })}
        onSelect={handleProtocolSelect}
        sourceNode={protocolModal.sourceNode}
        targetNode={protocolModal.targetNode}
      />

      {/* Main Canvas Area */}
      <div className="flex-1 flex flex-col overflow-hidden">

      {/* Top Metrics Bar */}
      <div className="flex items-center gap-2 px-4 py-2 bg-white border-b">
        <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 rounded text-blue-700">
          <TrendingUp className="w-3.5 h-3.5" />
          <span className="text-xs font-medium">{metrics.totalQPS.toLocaleString()} QPS</span>
          {challenge?.requirements?.qps_read && (meetsQPS ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />)}
        </div>
        <div className="flex items-center gap-1.5 px-2 py-1 bg-green-50 rounded text-green-700">
          <DollarSign className="w-3.5 h-3.5" />
          <span className="text-xs font-medium">${metrics.monthlyCost}/mo</span>
          {withinBudget ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
        </div>
        <div className="flex items-center gap-1.5 px-2 py-1 bg-purple-50 rounded text-purple-700">
          <Clock className="w-3.5 h-3.5" />
          <span className="text-xs font-medium">{metrics.avgLatency}ms</span>
        </div>
        <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50 rounded text-orange-700">
          <Package className="w-3.5 h-3.5" />
          <span className="text-xs font-medium">{metrics.componentCount} nodes • {edges.length} conn</span>
        </div>

        <div className="flex-1" />

        {/* Checklist */}
        <div className="flex gap-1.5">
          {[
            { label: 'Cache', done: hasCache },
            { label: 'DB', done: hasDatabase },
            { label: 'LB', done: hasLoadBalancer },
          ].map(({ label, done }) => (
            <span key={label} className={`px-2 py-0.5 rounded text-xs font-medium ${done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {label} {done && '✓'}
            </span>
          ))}
        </div>

        {!showHint && !hintUsed && (
          <button onClick={() => { setShowHint(true); setHintUsed(true); }}
            className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700 hover:bg-yellow-200 flex items-center gap-1">
            <HelpCircle className="w-3 h-3" /> Hint
          </button>
        )}

        {/* Fullscreen Toggle */}
        <button
          onClick={toggleFullscreen}
          className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 bg-slate-200 text-slate-700 hover:bg-slate-300"
          title={isFullscreen ? 'Exit fullscreen (F11)' : 'Enter fullscreen (F11)'}
        >
          {isFullscreen ? (
            <Minimize2 className="w-4 h-4" />
          ) : (
            <Maximize2 className="w-4 h-4" />
          )}
          {isFullscreen ? 'Exit' : 'Fullscreen'}
        </button>

        {/* Simulation Toggle */}
        <button
          onClick={() => {
            // If public preview, prompt login for simulation
            if (isPublicPreview && onLoginRequired) {
              onLoginRequired();
              return;
            }
            setShowSimulation(!showSimulation);
            if (!showSimulation) clearResults();
          }}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
            showSimulation
              ? 'bg-cyan-600 text-white'
              : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
          }`}
        >
          <FlaskConical className="w-4 h-4" />
          Simulate
        </button>

        {/* Toggle Solution Button or Unlock Button */}
        {solutionLocked ? (
          <button
            onClick={handleUnlockSolution}
            disabled={unlockingSolution || isPublicPreview}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
              unlockingSolution || isPublicPreview
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-300'
            }`}
            title={isPublicPreview ? 'Sign in to unlock' : `Unlock solution for ${solutionUnlockCost} DP`}
          >
            {unlockingSolution ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Lock className="w-4 h-4" />
            )}
            {unlockingSolution ? 'Unlocking...' : `Solution (${solutionUnlockCost} DP)`}
          </button>
        ) : referenceSolution && (
          <button
            onClick={handleToggleSolution}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
              viewingMode === 'solution'
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
            }`}
            title={viewingMode === 'solution' ? 'Return to your design' : 'View reference solution'}
          >
            <Eye className="w-4 h-4" />
            {viewingMode === 'solution' ? 'My Design' : 'Solution'}
          </button>
        )}

        <button
          onClick={handleClearCanvas}
          disabled={nodes.length === 0 || viewingMode === 'solution'}
          className={`p-1.5 rounded-lg transition-all ${
            nodes.length > 0 && viewingMode !== 'solution'
              ? 'bg-red-100 text-red-700 hover:bg-red-200'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
          title={viewingMode === 'solution' ? 'Switch to your design first' : 'Clear canvas'}
        >
          <Trash2 className="w-4 h-4" />
        </button>

        {/* Save Draft Button */}
        <button
          onClick={handleSaveDraft}
          disabled={nodes.length === 0 || isSaving || viewingMode === 'solution'}
          className={`p-1.5 rounded-lg transition-all ${
            nodes.length > 0 && !isSaving && viewingMode !== 'solution'
              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
          title={viewingMode === 'solution' ? 'Switch to your design first' : isSaving ? 'Saving...' : 'Save draft'}
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
        </button>

        {/* Save Message Toast */}
        {saveMessage && (
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            saveMessage.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {saveMessage.text}
          </span>
        )}

        <button onClick={handleSubmitClick} disabled={nodes.length === 0 || viewingMode === 'solution'}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
            nodes.length > 0 && viewingMode !== 'solution'
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
          title={viewingMode === 'solution' ? 'Switch to your design first' : 'Submit your design'}>
          Submit
        </button>
      </div>

      {/* Hint */}
      {showHint && (
        <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200 text-xs text-yellow-800">
          💡 Tip: Start with Load Balancer → Add Cache (Redis) → Connect to Database. Drag between handles to connect.
        </div>
      )}

      {/* Solution Viewing Mode Banner */}
      {viewingMode === 'solution' && (
        <div className="px-4 py-2 bg-purple-600 text-white text-sm flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span className="font-medium">Viewing Reference Solution</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopySolutionToCanvas}
              className="px-3 py-1 bg-emerald-500 text-white rounded font-medium text-xs hover:bg-emerald-600 transition-colors flex items-center gap-1"
            >
              <Copy className="w-3 h-3" />
              Copy to My Canvas
            </button>
            <button
              onClick={handleToggleSolution}
              className="px-3 py-1 bg-white text-purple-700 rounded font-medium text-xs hover:bg-purple-50 transition-colors"
            >
              ← Back
            </button>
          </div>
        </div>
      )}

      {/* Canvas and Simulation Sidebar */}
      <div className="flex-1 flex relative">
        {/* Canvas */}
        <div className={`flex-1 relative transition-all ${showSimulation ? 'mr-80' : ''}`}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            nodeTypes={nodeTypes}
            connectionMode="loose"
            fitView
            className="bg-gradient-to-br from-slate-100 to-blue-50"
          >
            <Background color="#ddd" gap={24} />
            <Controls className="!left-2 !bottom-2" />
            <MiniMap nodeColor={(node) => CATEGORY_COLORS[node.data.category] || '#6b7280'} className="!right-2 !bottom-2" maskColor="rgba(0,0,0,0.05)" />
          </ReactFlow>

          {/* Simulation Overlay - shows utilization on nodes during simulation */}
          {showSimulation && simulationState && reactFlowInstance && (
            <SimulationOverlay
              nodes={nodes}
              edges={edges}
              transform={[
                reactFlowInstance.getViewport().x,
                reactFlowInstance.getViewport().y,
                reactFlowInstance.getViewport().zoom,
              ]}
            />
          )}
        </div>

        {/* Simulation Sidebar */}
        {showSimulation && (
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-slate-900 border-l border-slate-700 overflow-y-auto">
            {/* Tabs */}
            <div className="flex border-b border-slate-700">
              <button
                onClick={() => setSimulationTab('traffic')}
                className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                  simulationTab === 'traffic'
                    ? 'bg-slate-800 text-cyan-400 border-b-2 border-cyan-400'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                <Activity className="w-4 h-4" />
                Traffic
              </button>
              <button
                onClick={() => setSimulationTab('chaos')}
                className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                  simulationTab === 'chaos'
                    ? 'bg-slate-800 text-orange-400 border-b-2 border-orange-400'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                <Flame className="w-4 h-4" />
                Chaos
              </button>
            </div>

            {/* Panel Content */}
            <div className="p-4">
              {simulationTab === 'traffic' ? (
                <SimulationPanel
                  designJson={getDesignJson()}
                  componentList={componentList}
                />
              ) : (
                <ChaosPanel
                  designJson={getDesignJson()}
                  componentList={componentList}
                  nodes={nodes}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Component Dock */}
      <ComponentDock
        inventory={(isPublicPreview || isAdminMode) ? allComponents : inventory}
        onAddComponent={addComponentToCanvas}
        isExpanded={dockExpanded}
        onToggle={() => setDockExpanded(!dockExpanded)}
        user={user}
        onShowUpgradeModal={() => setShowUpgradeModal(true)}
      />
      </div>

      {/* Validation Results Modal - shown on submit click */}
      <ValidationResultsModal
        isOpen={showValidationModal}
        onClose={() => !isSubmitting && setShowValidationModal(false)}
        result={validationResult}
        onSubmit={handleConfirmSubmit}
        isSubmitting={isSubmitting}
        isPublicPreview={isPublicPreview}
      />

      {/* Component Detail Modal - shown on node click */}
      {(selectedNodeDetails || componentDetailsLoading) && (
        <CanvasComponentDetailModal
          component={fullComponentDetails}
          loading={componentDetailsLoading}
          onClose={() => {
            setSelectedNodeDetails(null);
            setFullComponentDetails(null);
          }}
        />
      )}

      {/* Users Node Config Modal */}
      {usersNodeConfig && (
        <UsersNodeConfigModal
          config={usersNodeConfig}
          onSave={(newData) => handleUpdateUsersNode(usersNodeConfig.nodeId, newData)}
          onClose={() => setUsersNodeConfig(null)}
        />
      )}

      {/* Premium Upgrade Modal */}
      {showUpgradeModal && (
        <UpgradePromptModal onClose={() => setShowUpgradeModal(false)} />
      )}

      {/* Floating Fullscreen Exit Button */}
      {isFullscreen && (
        <button
          onClick={toggleFullscreen}
          className="fixed top-4 right-4 z-[10000] px-4 py-2 bg-slate-900/80 hover:bg-slate-900 text-white rounded-lg shadow-lg backdrop-blur-sm transition-all flex items-center gap-2 text-sm font-medium"
          title="Exit fullscreen (Esc or F11)"
        >
          <Minimize2 className="w-4 h-4" />
          Exit Fullscreen
        </button>
      )}

      {/* Tutorial Tips for Challenge 11 */}
      <TutorialOverlay
        isOpen={showTutorial}
        onComplete={handleTutorialComplete}
      />
    </div>
  );
};

export default DesignCanvas;
